'use client';

import Link from 'next/link';
import React from 'react';
import { formatPrice } from '@utils/price';
import { formatDuration } from '@lib/duration';
import { trackEvent } from '../../lib/analytics/posthog';
import { EVENTS } from '../../lib/analytics/events';
import CardRating from './CardRating';
import NextAvailable from './NextAvailable';
import Photo from './Photo';
import Ring from './Ring';

/** Mosaico de hasta 3 fotos: una grande y dos chicas (la tarjeta y la ficha del mapa lo comparten). */
export function PhotoMosaic({ photos, name, eager = false, className = '' }) {
  const shown = photos.slice(0, 3);
  return (
    <div className={`mosaic mosaic--${shown.length} ${className}`}>
      {shown.map((photo, i) => (
        <Photo
          key={photo.url}
          src={photo.url}
          size={i === 0 ? 500 : 250}
          alt={photo.caption ? `${photo.caption}, de ${name}` : `Trabajo de ${name}`}
          eager={eager && i === 0}
        />
      ))}
    </div>
  );
}

/**
 * Tarjeta de un negocio de Ágora: arriba su trabajo (hasta 3 fotos), el anillo, nombre, zona,
 * calificación, próximo turno y dos servicios con precio. Sin fotos, el logo o las iniciales ocupan
 * ese lugar (hoy la mayoría de los negocios todavía no subió fotos).
 */
const VendorCard = React.memo(function VendorCard({ item, priority = false, source = 'marketplace' }) {
  const handleClick = () => {
    const properties = {
      vendor_username: item.username || item.link?.replace('/negocios/', ''),
      source,
      card_type: 'vendor'
    };
    trackEvent(EVENTS.DISCOVERY_VENDOR_CARD_CLICKED, properties);
    if (source === 'directory_nearby') trackEvent(EVENTS.DIRECTORY_NEARBY_VENDOR_CLICKED, properties);
  };

  const photos = item.photos || [];
  const services = item.services || [];
  const hasPhotos = photos.length > 0;

  return (
    <article className={`vcard ${hasPhotos ? '' : 'vcard--plain'}`}>
      <Link href={item.link} className="vcard__link" onClick={handleClick}>
        {hasPhotos ? (
          <PhotoMosaic photos={photos} name={item.name} eager={priority} className="vcard__photos" />
        ) : (
          <div className="vcard__cover" aria-hidden="true">
            <Ring src={item.logo} name={item.name} size={108} fit="contain" />
          </div>
        )}

        <div className="vcard__body">
          {hasPhotos && <Ring src={item.logo} name={item.name} size={42} className="vcard__avatar" />}
          <h3 className="vcard__name">{item.name}</h3>
          {(item.area || item.neighborhood) && <p className="vcard__meta">{item.area || item.neighborhood}</p>}
          <div className="vcard__status">
            {item.username && <NextAvailable username={item.username} />}
            <CardRating agora={item.agora} google={item.google} />
          </div>
          <div className="vcard__services">
            {services.slice(0, 2).map((service, index) => (
              <div key={service.id || index} className="vcard__svc">
                <span className="vcard__svc-name">
                  {service.name}
                  {service.duration ? <small> · {formatDuration(service.duration)}</small> : null}
                </span>
                <span className="vcard__svc-price">{formatPrice(service.price, service.currency)}</span>
              </div>
            ))}
          </div>
          <div className="vcard__foot">
            <span className="vcard__cta">Ver negocio →</span>
            {services.length > 2 && <span className="vcard__count">{services.length} servicios</span>}
          </div>
        </div>
      </Link>
    </article>
  );
});

export default VendorCard;
